import React, { useCallback, useId, useState } from 'react';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { Avatar, Button, ListItemAvatar, ListItemText, ListSubheader, Menu, MenuItem } from '@mui/material';

export interface GeneratedOptionDescription {
  primary: string;
  secondary?: string;
  imageUrl?: string;
}

interface Props<T> {
  /** Heads the menu, describing what the options are */
  title: string;
  options: Array<T>;
  describe: (option: T) => GeneratedOptionDescription;
  /** Marks the option currently in the email */
  isSelected: (option: T) => boolean;
  onSelect: (option: T) => void;
}

const WRAP_TEXT = { sx: { whiteSpace: 'normal' } };

/**
 * The options generated for part of the email, to swap in place of the one in it.
 */
function GeneratedOptionsMenu<T>({ title, options, describe, isSelected, onSelect }: Props<T>) {
  const menuId = useId();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleSelect = useCallback(
    (option: T) => {
      setAnchorEl(null);
      onSelect(option);
    },
    [onSelect],
  );

  if (options.length < 2) {
    return null;
  }

  return (
    <>
      <Button
        variant="text"
        size="small"
        startIcon={<AutoAwesomeIcon />}
        endIcon={<ArrowDropDownIcon />}
        aria-haspopup="menu"
        aria-expanded={!!anchorEl}
        aria-controls={anchorEl ? menuId : undefined}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        AI options ({options.length})
      </Button>
      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { maxWidth: 560 } } }}
      >
        <ListSubheader>{title}</ListSubheader>
        {options.map((option, index) => {
          const { primary, secondary, imageUrl } = describe(option);
          return (
            // generated options can repeat each other's text, and the list doesn't change while the menu is open
            <MenuItem key={index} selected={isSelected(option)} onClick={() => handleSelect(option)}>
              {imageUrl && (
                <ListItemAvatar>
                  <Avatar variant="rounded" src={imageUrl} alt="" sx={{ width: 96, height: 54, mr: 2 }} />
                </ListItemAvatar>
              )}
              <ListItemText
                primary={primary}
                secondary={secondary}
                slotProps={{ primary: WRAP_TEXT, secondary: WRAP_TEXT }}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}

export default GeneratedOptionsMenu;
